import './utils/instrument'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import {
  ToolkitApp,
  ToolkitHome,
  ProvidersTrainingPage,
  ProvidersEchoPage,
  ClinicsResourcesPage,
  CommunityEducationPage,
  InstitutionPoliciesPage,
  ProvidersEducationalResourcesPage,
  ProvidersBurnoutSolutionsPage,
  ProvidersTreatmentPlansPage,
  ProvidersSectionPage,
  ClinicsTrainingPage,
  ClinicsEducationalResourcesPage,
  ClinicsEchoPage,
  ClinicsCommonElementsPage,
  ClinicsLocalResourcesPage,
  ClinicsPatientEducationPage,
  ClinicsAuditFeedbackPage,
  ClinicsSectionPage,
  ClinicsStaffReviewsPage,
  CommunityBasedResourcesPage,
  LimitedScdProvidersPage,
  CommunitySectionPage,
  CommunityEducationalMaterialsPage,
  EffectivenessOfInfusionClinicPage,
  CommunityBasedOrganizationsDetailPage,
  PoliticalCollaborationPage,
  ScdAwarenessStigmaPage,
  InstitutionSectionPage,
  InstitutionScdTeamEdRelationshipPage,
  InstitutionChampionsPage,
  InstitutionAuditFeedbackPage,
  InstitutionBuildingIcResourcesPage,
  InstitutionEstablishingScdClinicPage,
  InstitutionBusinessPlanChecklistPage
} from './toolkit/containers'
import {
  CulturalCompetencyTrainingPage,
  SocialDeterminantsHealthTrainingPage,
  PainManagementTrainingPage,
  ScdModifyingTreatmentsPage
} from './toolkit/containers/shared'
import { persistStore } from 'redux-persist'
import store from 'redux/store'
import { PersistGate } from 'redux-persist/integration/react'
import { Provider } from 'react-redux'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { ScrollToTop } from 'components'
import {
  App,
  ConsensusRecommendationsAdult,
  ConsensusRecommendationsTransformativeCenters,
  CenterMembershipCriteria,
  ConsensusRecommendations,
  ConsensusRecommendationPediatric,
  ConsensusRecommendationsInfant,
  ConsensusRecommendationsAcutePain,
  ExecutiveCommittee,
  HomePage,
  MemberCenterMap,
  LogoUsageGuidelines,
  ProviderResources,
  StartCenterInfo,
  AboutUs,
  OurPartners,
  Sponsors,
  JoinPage,
  GRNDaDAbout,
  GRNDaDLocations,
  GRNDaDExecutiveCommittee,
  GRNDaDBenefitsOfJoining,
  GRNDaDSpeakerSeries,
  PastEvent2025
} from 'containers'
import NotFound from './pages/NotFound'
import { CenterApplication } from './pages/CenterApplication'
// import { BannerProvider } from 'context'
import ProtectedRoute from './components/ProtectedRoute'
import AdminLayout from './components/admin/AdminLayout'
import AdminLogin from './pages/admin/Login'
import AdminDashboard from './pages/admin/Dashboard'
import ApplicationDetail from './pages/admin/ApplicationDetail'
import PendingApplications from './pages/admin/PendingApplications'
import ApprovedApplications from './pages/admin/ApprovedApplications'
import AllSubscribers from './pages/admin/AllSubscribers'
import Reports from './pages/admin/Reports'

const persistor = persistStore(store)
const container = document.getElementById('root') as HTMLDivElement
const root = createRoot(container, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack)
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler()
})

root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <Sentry.ErrorBoundary>
        {/* <BannerProvider> */}
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<App />}>
              <Route index element={<HomePage />} />
              <Route path="join" element={<JoinPage />} />
              <Route
                path="center_application/:type/:token"
                element={<CenterApplication />}
              />
              {/* Demo route for testing - remove in production */}
              <Route
                path="center-application-demo/:type"
                element={<CenterApplication />}
              />
              <Route
                path="center-membership-criteria"
                element={<CenterMembershipCriteria />}
              />
              <Route path="consensus-recommendations">
                <Route path="general" element={<ConsensusRecommendations />} />
                <Route
                  path={'adult'}
                  element={<ConsensusRecommendationsAdult />}
                />
                <Route
                  path="infant"
                  element={<ConsensusRecommendationsInfant />}
                />
                <Route
                  path="pediatric"
                  element={<ConsensusRecommendationPediatric />}
                />
                <Route
                  path="transformative-scd-center"
                  element={<ConsensusRecommendationsTransformativeCenters />}
                />
                <Route
                  path="acute-pain"
                  element={<ConsensusRecommendationsAcutePain />}
                />
              </Route>
              <Route
                path="member-center-map"
                element={<MemberCenterMap onlyAssociate={false} />}
              />
              <Route
                path="associate-center-map"
                element={<MemberCenterMap onlyAssociate={true} />}
              />
              <Route
                path="logo-usage-guidelines"
                element={<LogoUsageGuidelines />}
              />
              {/* <Route path="consensus-event" element={<ConsensusEvent />} /> */}
              <Route
                path="provider-resources"
                element={<ProviderResources />}
              />
              <Route path="start-a-center" element={<StartCenterInfo />} />
              <Route path="about-us" element={<AboutUs />} />
              <Route
                path="executive-committee"
                element={<ExecutiveCommittee />}
              />
              <Route path="our-partners" element={<OurPartners />} />
              <Route path="sponsors" element={<Sponsors />} />
              {/* Past Events */}
              <Route path="past-events/2025" element={<PastEvent2025 />} />
              {/* GRNDaD routes */}
              <Route path="grndad">
                <Route path="about" element={<GRNDaDAbout />} />
                <Route path="locations" element={<GRNDaDLocations />} />
                <Route
                  path="executive-committee"
                  element={<GRNDaDExecutiveCommittee />}
                />
                <Route
                  path="benefits-of-joining"
                  element={<GRNDaDBenefitsOfJoining />}
                />
                <Route
                  path="speaker-series"
                  element={<GRNDaDSpeakerSeries />}
                />
                <Route index element={<GRNDaDAbout />} />
              </Route>
              {/* Toolkit routes */}
              <Route path="toolkit" element={<ToolkitApp />}>
                <Route index element={<ToolkitHome />} />
                {/* Shared routes accessible from multiple modules */}
                <Route
                  path="shared/cultural-competency-training"
                  element={<CulturalCompetencyTrainingPage />}
                />
                <Route
                  path="shared/social-determinants-health-training"
                  element={<SocialDeterminantsHealthTrainingPage />}
                />
                <Route
                  path="shared/pain-management-training"
                  element={<PainManagementTrainingPage />}
                />
                <Route
                  path="shared/scd-modifying-treatments"
                  element={<ScdModifyingTreatmentsPage />}
                />
                <Route path="providers" element={<ProvidersSectionPage />} />
                <Route
                  path="providers/training"
                  element={<ProvidersTrainingPage />}
                />
                <Route path="providers/echo" element={<ProvidersEchoPage />} />
                <Route
                  path="providers/educational-resources"
                  element={<ProvidersEducationalResourcesPage />}
                />
                <Route
                  path="providers/burnout-solutions"
                  element={<ProvidersBurnoutSolutionsPage />}
                />
                <Route
                  path="providers/treatment-plans"
                  element={<ProvidersTreatmentPlansPage />}
                />
                <Route path="clinics" element={<ClinicsSectionPage />} />
                <Route
                  path="clinics/training"
                  element={<ClinicsTrainingPage />}
                />
                <Route
                  path="clinics/resources"
                  element={<ClinicsResourcesPage />}
                />
                <Route
                  path="clinics/educational-resources"
                  element={<ClinicsEducationalResourcesPage />}
                />
                <Route
                  path="clinics/treatment-plans"
                  element={<ProvidersTreatmentPlansPage />}
                />
                <Route path="clinics/echo" element={<ClinicsEchoPage />} />
                <Route
                  path="clinics/common-elements"
                  element={<ClinicsCommonElementsPage />}
                />
                <Route
                  path="clinics/local-resources"
                  element={<ClinicsLocalResourcesPage />}
                />
                <Route
                  path="clinics/patient-education"
                  element={<ClinicsPatientEducationPage />}
                />
                <Route
                  path="clinics/audit-feedback"
                  element={<ClinicsAuditFeedbackPage />}
                />
                <Route
                  path="clinics/staff-reviews"
                  element={<ClinicsStaffReviewsPage />}
                />
                <Route
                  path="community/community-based-resources"
                  element={<CommunityBasedResourcesPage />}
                />
                <Route
                  path="community/limited-scd-providers"
                  element={<LimitedScdProvidersPage />}
                />
                <Route
                  path="community/community-educational-materials"
                  element={<CommunityEducationalMaterialsPage />}
                />
                <Route
                  path="community/effectiveness-of-infusion-clinic"
                  element={<EffectivenessOfInfusionClinicPage />}
                />
                <Route
                  path="community/community-based-organizations-detail"
                  element={<CommunityBasedOrganizationsDetailPage />}
                />
                <Route
                  path="community/political-collaboration"
                  element={<PoliticalCollaborationPage />}
                />
                <Route
                  path="community/scd-awareness-stigma"
                  element={<ScdAwarenessStigmaPage />}
                />
                <Route path="community" element={<CommunitySectionPage />} />
                <Route
                  path="community/education"
                  element={<CommunityEducationPage />}
                />
                <Route
                  path="institution"
                  element={<InstitutionSectionPage />}
                />
                <Route
                  path="institution/scd-team-ed-relationship"
                  element={<InstitutionScdTeamEdRelationshipPage />}
                />
                <Route
                  path="institution/champions"
                  element={<InstitutionChampionsPage />}
                />
                <Route
                  path="institution/audit-feedback"
                  element={<InstitutionAuditFeedbackPage />}
                />
                <Route
                  path="institution/building-ic-resources"
                  element={<InstitutionBuildingIcResourcesPage />}
                />
                <Route
                  path="institution/establishing-scd-clinic"
                  element={<InstitutionEstablishingScdClinicPage />}
                />
                <Route
                  path="institution/business-plan-checklist"
                  element={<InstitutionBusinessPlanChecklistPage />}
                />
                <Route
                  path="institution/policies"
                  element={<InstitutionPoliciesPage />}
                />
              </Route>
            </Route>

            <Route path="admin">
              <Route path="login" element={<AdminLogin />} />
              <Route
                path=""
                element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route
                  path="pending-applications"
                  element={<PendingApplications />}
                />
                <Route
                  path="approved-applications"
                  element={<ApprovedApplications />}
                />
                <Route
                  path="applications/:id"
                  element={<ApplicationDetail />}
                />
                <Route path="subscribers" element={<AllSubscribers />} />
                <Route path="reports" element={<Reports />} />
              </Route>
            </Route>
            {/* Catch-all route for 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        {/* </BannerProvider> */}
      </Sentry.ErrorBoundary>
    </PersistGate>
  </Provider>
)
